/**
 * GuidesCard.tsx — the dashboard "Your guides" block: the banner (when a
 * pending step is genuinely the patient's) and the guides card itself.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: engagement unlock (2026-09-23).
 * Built against the DashboardGuides board (CTO-approved) and the pinned
 * engagement wire contract's addendum-2 `mine` pair.
 *
 * A VIEW UNTIL THE STEP IS DONE. Locked tiles are VISIBLE (titles, dashed
 * "Unlocks with your plan" affordance routing into the pending step) — never
 * disabled-grey, never hidden: the guides are what the patient was promised,
 * and the tile is the promise restated with its condition. Only the server
 * flipping the ladder to `unlocked` makes Download appear, and every download
 * click re-derives server-side anyway (R41). The judgements live in
 * `guidesModel.ts`; read its header for what renders nothing and why
 * `waiting` gets tiles but no banner.
 *
 * THE CTA REUSES THE DASHBOARD'S OWN CONTINUATION ROUTE. `continueHref` is
 * computed by the Dashboard from the same `resumeHref` rule the order rows
 * use (a resumable order → the signed-in `#resume=` entry), falling back to
 * the journey page — this card invents no second definition of "where does
 * this patient pick up".
 *
 * FAIL-SOFT, NEVER FAIL-SILENT-ABOUT-AN-OBLIGATION: a failed read renders
 * nothing, which here claims nothing — the card is additive content, not the
 * dashboard's is-anything-needed answer (that stays `dashboardSummary`'s).
 *
 * ⚠️ The phrase "engagement content" appears NOWHERE patient-facing — these
 * are "your guides" everywhere a patient reads (the spec sweeps for it).
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { downloadMyEngagementContent, getMyEngagement } from '../api/engagement';
import type { MyEngagement, UnlockItem } from '../api/types';
import { openSignedDocument } from '../lib/signedDocument';
import { UNLOCK_DOWNLOAD_FAILED_MESSAGE, UNLOCK_UNCONFIRMED_MESSAGE } from '../unlock/UnlockPage';
import { Button, Card, buttonClass, buttonStyle } from '../ui/kit';
import { CardIconHeader } from '../ui/sections';
import { guidesView } from './guidesModel';

type TileOutcome = 'opened' | 'unconfirmed' | 'failed' | 'superseded';

export function GuidesCard({ continueHref }: { continueHref: string }) {
  // undefined = read in flight; null = the read failed (both render nothing —
  // see guidesModel's header for why absence is safe HERE).
  const [mine, setMine] = useState<MyEngagement | null | undefined>(undefined);
  const [readNonce, setReadNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getMyEngagement().then(
      (result) => {
        if (!cancelled) setMine(result);
      },
      () => {
        if (!cancelled) setMine(null);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [readNonce]);

  /** Re-read the ladder — the only honest refresh after a `locked` answer. */
  const reRead = useCallback(() => {
    setReadNonce((n) => n + 1);
  }, []);

  const download = useCallback(
    async (contentId: string): Promise<TileOutcome> => {
      try {
        const result = await downloadMyEngagementContent(contentId);
        if (result.outcome === 'url') {
          openSignedDocument(result.url);
          return 'opened';
        }
        if (result.outcome === 'unconfirmed') return 'unconfirmed';
        // 'locked': the server re-derived the state on THIS call — re-read and
        // render whatever the ladder now says, never argue with a stale card.
        reRead();
        return 'superseded';
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) {
          reRead();
          return 'superseded';
        }
        return 'failed';
      }
    },
    [reRead],
  );

  const view = guidesView(mine);
  if (view.kind === 'hidden') return null;

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-4)', gridColumn: '1 / -1' }}>
      {view.kind === 'locked' && view.banner === 'plan_waiting' ? <PlanWaitingBanner continueHref={continueHref} /> : null}
      {view.kind === 'locked' && view.banner === 'plan_ended' ? <PlanEndedBanner continueHref={continueHref} /> : null}
      <Card>
        <CardIconHeader
          icon={<TileDocGlyph />}
          title="Your guides"
          subtitle={
            view.kind === 'unlocked'
              ? 'From your care team — yours to download.'
              : 'From your care team — they unlock with your plan.'
          }
        />
        <div className="gps-dashboard-grid" style={{ display: 'grid', gap: 'var(--gps-s-3)', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          {view.items.map((item) =>
            view.kind === 'unlocked' ? (
              <UnlockedTile key={item.contentId} item={item} onDownload={download} />
            ) : (
              <LockedTile key={item.contentId} item={item} continueHref={continueHref} />
            ),
          )}
          <MoreOnTheWayTile />
        </div>
      </Card>
    </div>
  );
}

/**
 * The pending-step banner — rendered ONLY when the pending step is genuinely
 * the patient's (guidesModel's `banner`). The CTA is a real navigation into
 * the dashboard's existing continuation route, never a second machine.
 */
function PlanWaitingBanner({ continueHref }: { continueHref: string }) {
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
        <span
          aria-hidden="true"
          style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'var(--gps-warn-text)', flex: 'none' }}
        />
        <span style={{ fontSize: '14.5px', lineHeight: 1.5, flexGrow: 1, minWidth: '200px' }}>
          <strong>Your plan is waiting.</strong>{' '}
          <span style={{ color: 'var(--gps-muted)' }}>
            Finish setting it up — about 2 minutes — and your guides unlock too.
          </span>
        </span>
        <Link
          to={continueHref}
          className={buttonClass('primary')}
          style={{ ...buttonStyle('primary'), width: 'auto', flex: 'none' }}
        >
          Finish setting up
        </Link>
      </div>
    </Card>
  );
}

/**
 * The honest lapsed banner — mirrors the /unlock page's own LapsedView
 * framing. A lapsed plan is OVER: "Your plan is waiting… about 2 minutes"
 * would be false copy about a plan with nothing left to set up (see
 * guidesModel's header). The restart CTA routes through the SAME dashboard
 * continuation the waiting banner uses — this card invents no second
 * definition of "where does this patient pick up".
 */
function PlanEndedBanner({ continueHref }: { continueHref: string }) {
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
        <span
          aria-hidden="true"
          style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'var(--gps-muted)', flex: 'none' }}
        />
        <span style={{ fontSize: '14.5px', lineHeight: 1.5, flexGrow: 1, minWidth: '200px' }}>
          <strong>Your plan has ended.</strong>{' '}
          <span style={{ color: 'var(--gps-muted)' }}>
            Your guides lock when a plan ends — restart your plan and they unlock again.
          </span>
        </span>
        <Link
          to={continueHref}
          className={buttonClass('primary')}
          style={{ ...buttonStyle('primary'), width: 'auto', flex: 'none' }}
        >
          Restart my plan
        </Link>
      </div>
    </Card>
  );
}

/**
 * A locked tile: the guide by name, with the DASHED, VISIBLE "Unlocks with
 * your plan" affordance — a real link into the pending step, never a
 * disabled-grey control (a disabled tile says "broken"; this one says what
 * makes it open). No sent date and no size: neither rides the `mine` wire,
 * and this portal never renders a date or figure the server never sent.
 */
function LockedTile({ item, continueHref }: { item: UnlockItem; continueHref: string }) {
  return (
    <div
      style={{
        border: '1px solid var(--gps-border)',
        borderRadius: 'var(--gps-r-md)',
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <TileLockGlyph />
      <span style={{ fontWeight: 600, fontSize: '14.5px', lineHeight: 1.35, color: 'var(--gps-text)' }}>{item.title}</span>
      <span style={{ fontSize: '12.5px', color: 'var(--gps-muted)' }}>PDF guide · from your care team</span>
      <Link
        to={continueHref}
        aria-label={`${item.title} — unlocks with your plan`}
        style={{
          marginTop: 'auto',
          fontWeight: 600,
          fontSize: '13px',
          textDecoration: 'none',
          color: 'var(--gps-muted)',
          border: '1px dashed var(--gps-border)',
          borderRadius: 'var(--gps-r-md)',
          padding: '8px 0',
          textAlign: 'center',
        }}
      >
        Unlocks with your plan
      </Link>
    </div>
  );
}

/** An unlocked tile: the same guide, now with its Download. */
function UnlockedTile({
  item,
  onDownload,
}: {
  item: UnlockItem;
  onDownload: (contentId: string) => Promise<TileOutcome>;
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  async function press() {
    if (busy) return;
    setBusy(true);
    setNote(null);
    const outcome = await onDownload(item.contentId);
    // 'superseded' sets nothing — the card is re-reading and this tile is
    // about to be replaced by whatever the server now says.
    if (outcome === 'unconfirmed') setNote(UNLOCK_UNCONFIRMED_MESSAGE);
    else if (outcome === 'failed') setNote(UNLOCK_DOWNLOAD_FAILED_MESSAGE);
    setBusy(false);
  }

  return (
    <div
      style={{
        border: '1px solid var(--gps-border)',
        borderRadius: 'var(--gps-r-md)',
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <TileDocGlyph />
      <span style={{ fontWeight: 600, fontSize: '14.5px', lineHeight: 1.35, color: 'var(--gps-text)' }}>{item.title}</span>
      <span style={{ fontSize: '12.5px', color: 'var(--gps-muted)' }}>PDF guide</span>
      <Button
        type="button"
        variant="ghost"
        disabled={busy}
        onClick={() => void press()}
        aria-label={`Download ${item.title}`}
        style={{ marginTop: 'auto', minHeight: '38px' }}
      >
        {busy ? 'Opening…' : 'Download'}
      </Button>
      {note ? (
        <p role="status" style={{ margin: 0, fontSize: '12.5px', lineHeight: 1.5, color: 'var(--gps-text)' }}>
          {note}
        </p>
      ) : null}
    </div>
  );
}

/** The board's standing placeholder — guides get added as the plan goes on. */
function MoreOnTheWayTile() {
  return (
    <div
      style={{
        border: '1px dashed var(--gps-border)',
        borderRadius: 'var(--gps-r-md)',
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        justifyContent: 'center',
        color: 'var(--gps-muted)',
        fontSize: '13px',
        lineHeight: 1.5,
      }}
    >
      <span style={{ fontWeight: 600 }}>More on the way</span>
      <span>Your care team adds guides as your plan goes on.</span>
    </div>
  );
}

function TileLockGlyph() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--gps-muted)"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="5" y="10" width="14" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0" />
    </svg>
  );
}

function TileDocGlyph() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--gps-primary)"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
      <path d="M14 3v5h5" />
    </svg>
  );
}
